import React, { useState } from "react";
import { Button } from "./ui/button";
import { Menu, X, AlertTriangle } from "lucide-react";

interface NavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

export function Navigation({ currentPage, onPageChange }: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { name: "Home", key: "home", href: "/" },
    { name: "Who We Are", key: "who-we-are", href: "/who-we-are" },
    { name: "Completed Projects", key: "projects", href: "/projects" },
    { name: "Area of Expertise", key: "expertise", href: "/expertise" },
    { name: "Contact Us", key: "contact", href: "/contact" },
    { name: "Careers", key: "careers", href: "/careers" },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, page: string) => {
    e.preventDefault();
    onPageChange(page);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white border-b border-border shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <a
                href="/"
                onClick={(e) => handleNavClick(e, "home")}
                className="flex items-center space-x-2"
                aria-label="USP Consultancy - Go to homepage"
              >
                <img
                  src="/usp.png"
                  alt="USP Consultancy Logo"
                  className="h-24 w-auto cursor-pointer"
                  width="96"
                  height="96"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.key)}
                    className={`px-3 py-2 rounded-md transition-colors cursor-pointer ${currentPage === item.key
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button variant="ghost" size="sm" onClick={toggleMobileMenu}>
                {isMobileMenuOpen ? <X /> : <Menu />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && ( //  use conditional render for smooth toggle
            <div className="md:hidden bg-white border-t border-border">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                {navItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.key)}
                    className={`block px-3 py-2 rounded-md w-full text-left transition-colors ${currentPage === item.key
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Development Banner
        <div
          className="py-2 px-4 text-center"
          style={{ backgroundColor: "#dc2626" }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center space-x-2 text-xs md:text-sm">
              <AlertTriangle
                className="w-4 h-4 flex-shrink-0"
                style={{ color: "white" }}
              />
              <span
                className="font-medium"
                style={{
                  color: "white",
                  animation: "blink 1.5s infinite ease-in-out",
                }}
              >
                🚧 This website is under development and testing phase. It will
                be fully functional soon!
              </span>
            </div>
          </div>
        </div> */}
      </nav>

      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0.4; }
          }
        `}
      </style>
    </>
  );
}
